/**
 * techLabel — turn a project's tech_type_id into a short, human-readable
 * label suitable for a table cell or badge.
 *
 * Handles both:
 *   - The short-form IDs accepted by the API: `solar_ground`, `solar_rooftop`,
 *     `wind_onshore`, `wind_offshore`.
 *   - The full ecoinvent activity strings stored on legacy projects, e.g.
 *     `"electricity production, wind, >3MW turbine, onshore"` or
 *     `"3kWp slanted-roof installation, multi-Si, panel, mounted"`.
 *
 * Returns the original string as a fallback when nothing matches so the UI
 * still surfaces something rather than blanking out.
 */

const SHORT_FORM_LABELS: Record<string, string> = {
  solar_ground: "Solar",
  solar_rooftop: "Solar (Rooftop)",
  wind_onshore: "Wind",
  wind_offshore: "Wind (Offshore)",
};

export function techLabel(techType?: string | null): string {
  if (!techType) return "—";
  if (SHORT_FORM_LABELS[techType]) return SHORT_FORM_LABELS[techType];

  const lower = techType.toLowerCase();
  // Long-form ecoinvent activity names — keyword-match by category.
  if (lower.includes("offshore") && lower.includes("wind")) return "Wind (Offshore)";
  if (lower.includes("wind")) return "Wind";
  if (lower.includes("slanted-roof") || lower.includes("rooftop")) return "Solar (Rooftop)";
  if (lower.includes("open ground") || lower.includes("photovoltaic") ||
      lower.includes("solar") || lower.includes("multi-si") || lower.includes("single-si")) {
    return "Solar";
  }
  if (lower.includes("battery") || lower.includes("storage")) return "Storage";
  // Fallback: return the original string (caller can truncate if needed).
  return techType;
}
